<script>
    import { onMount } from 'svelte';
    import { db, DB_TABLES, auth } from './providers/index.js';
    
    export let user = null;
    
    let todos = [];
    let newTodoText = '';
    let filter = 'all'; // 'all', 'active', 'completed'
    let editingId = null;
    let editText = '';
    let isLoading = true;
    let error = null;
    
    // Load todos from Supabase on mount and when user changes
    $: if (user) {
        loadTodos();
    } else {
        todos = [];
        isLoading = false;
    }
    
    async function loadTodos() {
        if (!user) return;
        
        isLoading = true;
        error = null;
        
        try {
            const result = await db
                .from(DB_TABLES.TODOS)
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .exec();
            
            if (result.error) throw result.error;
            
            // 转换数据格式以兼容现有代码
            todos = result.data.map(todo => ({
                id: todo.id,
                text: todo.title,
                completed: todo.completed,
                createdAt: todo.created_at
            }));
        } catch (err) {
            console.error('加载待办事项失败:', err);
            error = '加载失败，请刷新页面重试';
        } finally {
            isLoading = false;
        }
    }
    
    // Computed properties
    $: filteredTodos = getFilteredTodos(todos, filter);
    $: activeCount = todos.filter(todo => !todo.completed).length;
    $: completedCount = todos.filter(todo => todo.completed).length;
    $: allCompleted = todos.length > 0 && todos.every(todo => todo.completed);
    
    function getFilteredTodos(todos, filter) {
        switch (filter) {
            case 'active':
                return todos.filter(todo => !todo.completed);
            case 'completed':
                return todos.filter(todo => todo.completed);
            default:
                return todos;
        }
    }
    
    async function addTodo() {
        const text = newTodoText.trim();
        if (!text || !user) return;
        
        try {
            const result = await db
                .from(DB_TABLES.TODOS)
                .insert({
                    user_id: user.id,
                    title: text,
                    completed: false
                });
            
            if (result.error) throw result.error;
            
            const data = result.data;
            // 添加到本地状态
            todos = [{
                id: data.id,
                text: data.title,
                completed: data.completed,
                createdAt: data.created_at
            }, ...todos];
            
            newTodoText = '';
        } catch (err) {
            console.error('添加待办事项失败:', err);
            error = '添加失败，请重试';
        }
    }
    
    async function toggleTodo(id) {
        if (!user) return;
        
        const todo = todos.find(t => t.id === id);
        if (!todo) return;
        
        try {
            const newCompletedStatus = !todo.completed;
            
            const result = await db
                .from(DB_TABLES.TODOS)
                .update({ completed: newCompletedStatus })
                .eq('id', id)
                .eq('user_id', user.id)
                .updateExec();
            
            if (result.error) throw result.error;
            
            // 更新本地状态
            todos = todos.map(t =>
                t.id === id ? { ...t, completed: newCompletedStatus } : t
            );
        } catch (err) {
            console.error('更新待办事项失败:', err);
            error = '更新失败，请重试';
        }
    }
    
    async function deleteTodo(id) {
        if (!user) return;
        
        try {
            const result = await db
                .from(DB_TABLES.TODOS)
                .eq('id', id)
                .eq('user_id', user.id)
                .delete();
            
            if (result.error) throw result.error;
            
            // 从本地状态中移除
            todos = todos.filter(todo => todo.id !== id);
        } catch (err) {
            console.error('删除待办事项失败:', err);
            error = '删除失败，请重试';
        }
    }
    
    function startEditing(id, text) {
        editingId = id;
        editText = text;
    }
    
    async function saveEdit() {
        if (!editText.trim() || editingId === null || !user) {
            cancelEdit();
            return;
        }
        
        try {
            const result = await db
                .from(DB_TABLES.TODOS)
                .update({ title: editText.trim() })
                .eq('id', editingId)
                .eq('user_id', user.id)
                .updateExec();
            
            if (result.error) throw result.error;
            
            // 更新本地状态
            todos = todos.map(todo =>
                todo.id === editingId ? { ...todo, text: editText.trim() } : todo
            );
        } catch (err) {
            console.error('更新待办事项失败:', err);
            error = '更新失败，请重试';
        }
        
        cancelEdit();
    }
    
    function cancelEdit() {
        editingId = null;
        editText = '';
    }
    
    async function toggleAll() {
        if (!user) return;
        
        const shouldComplete = !allCompleted;
        const todoIds = todos.map(todo => todo.id);
        
        if (todoIds.length === 0) return;
        
        try {
            const result = await db
                .from(DB_TABLES.TODOS)
                .update({ completed: shouldComplete })
                .eq('user_id', user.id)
                .in('id', todoIds)
                .updateExec();
            
            if (result.error) throw result.error;
            
            // 更新本地状态
            todos = todos.map(todo => ({ ...todo, completed: shouldComplete }));
        } catch (err) {
            console.error('批量更新失败:', err);
            error = '批量更新失败，请重试';
        }
    }
    
    async function clearCompleted() {
        if (!user) return;
        
        const completedTodoIds = todos
            .filter(todo => todo.completed)
            .map(todo => todo.id);
        
        if (completedTodoIds.length === 0) return;
        
        try {
            const result = await db
                .from(DB_TABLES.TODOS)
                .eq('user_id', user.id)
                .in('id', completedTodoIds)
                .delete();
            
            if (result.error) throw result.error;
            
            // 从本地状态中移除
            todos = todos.filter(todo => !todo.completed);
        } catch (err) {
            console.error('批量删除失败:', err);
            error = '批量删除失败，请重试';
        }
    }
    
    function handleKeydown(event) {
        if (event.key === 'Enter') {
            addTodo();
        }
    }
    
    function handleEditKeydown(event) {
        if (event.key === 'Enter') {
            saveEdit();
        } else if (event.key === 'Escape') {
            cancelEdit();
        }
    }
    
    async function handleLogout() {
        try {
            const result = await auth.signOut();
            if (result.error) throw result.error;
            
            // 清除本地状态
            todos = [];
            newTodoText = '';
            filter = 'all';
            editingId = null;
            editText = '';
            error = null;
            
            // 通知父组件用户已登出
            dispatch('logout');
        } catch (err) {
            console.error('登出失败:', err);
            error = '登出失败，请重试';
        }
    }
    
    import { createEventDispatcher } from 'svelte';
    const dispatch = createEventDispatcher();
</script>

<div class="todo-container">
    <!-- User info and logout -->
    <div class="user-header">
        <div class="user-info">
            <span class="welcome-text">欢迎，{user?.user_metadata?.username || user?.email}</span>
        </div>
        <button class="logout-button" on:click={handleLogout}>
            登出
        </button>
    </div>
    
    <!-- Error message -->
    {#if error}
        <div class="error-banner">
            {error}
            <button class="error-close" on:click={() => error = null}>×</button>
        </div>
    {/if}
    <!-- Loading state -->
    {#if isLoading}
        <div class="loading-state">
            <div class="spinner"></div>
            <p>加载中...</p>
        </div>
    {:else}
        <!-- Add new todo -->
        <div class="todo-input-container">
            {#if todos.length > 0}
                <input
                    type="checkbox"
                    class="toggle-all"
                    checked={allCompleted}
                    on:change={toggleAll}
                    title="Toggle all todos"
                />
            {/if}
            <input
                type="text"
                class="todo-input"
                placeholder="What needs to be done?"
                bind:value={newTodoText}
                on:keydown={handleKeydown}
            />
        </div>
    
    <!-- Todo list -->
    <ul class="todo-list">
        {#each filteredTodos as todo (todo.id)}
            <li class="todo-item" class:completed={todo.completed} class:editing={editingId === todo.id}>
                <div class="todo-view">
                    <input
                        type="checkbox"
                        class="todo-checkbox"
                        id="todo-{todo.id}"
                        checked={todo.completed}
                        on:change={() => toggleTodo(todo.id)}
                    />
                    <label
                        class="todo-text"
                        class:completed={todo.completed}
                        for="todo-{todo.id}"
                        on:dblclick={() => startEditing(todo.id, todo.text)}
                    >
                        {todo.text}
                    </label>
                    <button
                        class="todo-delete"
                        on:click={() => deleteTodo(todo.id)}
                        title="Delete todo"
                    >
                        ×
                    </button>
                </div>
                {#if editingId === todo.id}
                    <input
                        type="text"
                        class="todo-edit-input"
                        bind:value={editText}
                        on:keydown={handleEditKeydown}
                        on:blur={saveEdit}
                        use:focus
                    />
                {/if}
            </li>
        {/each}
    </ul>
    
    <!-- Footer with stats and filters -->
    {#if todos.length > 0}
        <footer class="todo-footer">
            <span class="todo-count">
                <strong>{activeCount}</strong>
                {activeCount === 1 ? 'item' : 'items'} left
            </span>
            
            <ul class="todo-filters">
                <li>
                    <button
                        class="filter-btn"
                        class:selected={filter === 'all'}
                        on:click={() => filter = 'all'}
                    >
                        All
                    </button>
                </li>
                <li>
                    <button
                        class="filter-btn"
                        class:selected={filter === 'active'}
                        on:click={() => filter = 'active'}
                    >
                        Active
                    </button>
                </li>
                <li>
                    <button
                        class="filter-btn"
                        class:selected={filter === 'completed'}
                        on:click={() => filter = 'completed'}
                    >
                        Completed
                    </button>
                </li>
            </ul>
            
            {#if completedCount > 0}
                <button
                    class="clear-completed"
                    on:click={clearCompleted}
                >
                    Clear completed
                </button>
            {/if}
        </footer>
    {/if}
    
    <!-- Empty state -->
    {#if todos.length === 0}
        <div class="empty-state">
            <p>🎉 No todos yet! Add one above to get started.</p>
        </div>
    {/if}
    {/if}
</div>

<style>
    .todo-container {
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }
    
    .user-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
    }
    
    .user-info {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .welcome-text {
        font-size: 14px;
        color: #495057;
        font-weight: 500;
    }
    
    .logout-button {
        background: #dc3545;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 6px 12px;
        font-size: 12px;
        cursor: pointer;
        transition: background-color 0.2s;
    }
    
    .logout-button:hover {
        background: #c82333;
    }
    
    .error-banner {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: #f8d7da;
        color: #721c24;
        padding: 12px 20px;
        border-bottom: 1px solid #f5c6cb;
        font-size: 14px;
    }
    
    .error-close {
        background: none;
        border: none;
        color: #721c24;
        font-size: 18px;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.2s;
    }
    
    .error-close:hover {
        background: #f1b0b7;
    }
    
    .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        color: #6c757d;
    }
    
    .loading-state .spinner {
        width: 32px;
        height: 32px;
        border: 3px solid #e9ecef;
        border-top: 3px solid #007bff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 16px;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    .todo-input-container {
        display: flex;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e9ecef;
    }
    
    .toggle-all {
        width: 20px;
        height: 20px;
        margin-right: 12px;
        cursor: pointer;
    }
    
    .todo-input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 16px;
        padding: 8px 0;
        background: transparent;
    }
    
    .todo-input::placeholder {
        color: #adb5bd;
    }
    
    .todo-list {
        list-style: none;
        margin: 0;
        padding: 0;
    }
    
    .todo-item {
        border-bottom: 1px solid #e9ecef;
        position: relative;
    }
    
    .todo-item:last-child {
        border-bottom: none;
    }
    
    .todo-item.editing .todo-view {
        display: none;
    }
    
    .todo-view {
        display: flex;
        align-items: center;
        padding: 12px 20px;
    }
    
    .todo-checkbox {
        width: 18px;
        height: 18px;
        margin-right: 12px;
        cursor: pointer;
    }
    
    .todo-text {
        flex: 1;
        font-size: 16px;
        line-height: 1.4;
        word-break: break-word;
        cursor: pointer;
        user-select: none;
    }
    
    .todo-text.completed {
        text-decoration: line-through;
        color: #6c757d;
    }
    
    .todo-delete {
        background: none;
        border: none;
        font-size: 20px;
        color: #dc3545;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        opacity: 0;
        transition: opacity 0.2s, background-color 0.2s;
    }
    
    .todo-item:hover .todo-delete {
        opacity: 1;
    }
    
    .todo-delete:hover {
        background-color: #f8d7da;
    }
    
    .todo-edit-input {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: 1px solid #007bff;
        outline: none;
        font-size: 16px;
        padding: 12px 20px;
        background: white;
        z-index: 1;
    }
    
    .todo-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 20px;
        background: #f8f9fa;
        font-size: 14px;
        color: #6c757d;
    }
    
    .todo-count {
        flex: 1;
    }
    
    .todo-filters {
        display: flex;
        list-style: none;
        margin: 0;
        padding: 0;
        gap: 8px;
    }
    
    .filter-btn {
        background: none;
        border: none;
        padding: 4px 8px;
        cursor: pointer;
        color: #6c757d;
        border-radius: 4px;
        transition: color 0.2s, background-color 0.2s;
    }
    
    .filter-btn:hover {
        color: #495057;
        background-color: #e9ecef;
    }
    
    .filter-btn.selected {
        color: #007bff;
        border: 1px solid #007bff;
    }
    
    .clear-completed {
        background: none;
        border: none;
        color: #6c757d;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        transition: color 0.2s, background-color 0.2s;
    }
    
    .clear-completed:hover {
        color: #dc3545;
        background-color: #f8d7da;
    }
    
    .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: #6c757d;
        font-size: 16px;
    }
    
    /* Responsive design */
    @media (max-width: 600px) {
        .todo-footer {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
        }
        
        .todo-filters {
            width: 100%;
            justify-content: center;
        }
    }
</style>